import imageCompression from 'browser-image-compression';
// The library's Web Worker loads the library into itself via importScripts().
// Its default libURL is the jsDelivr CDN, which our CSP script-src blocks, so
// the worker fails. Point it at the copy bundled on our own origin (script-src
// 'self') so the worker can do the compressing.
import biclibUrl from 'browser-image-compression/dist/browser-image-compression.js?url';

const DEFAULT_QUALITY = 0.8;
const KEEP_ORIGINAL_THRESHOLD = 0.9;
const JPEG_MIME_TYPE = 'image/jpeg';

// Swap the extension for .jpg, keeping the rest of the name. Handles names with
// no extension (scan), dotfiles (.portrait), and trailing dots (photo.) without
// losing the base name.
const toJpgName = (name) => {
  const base = name.replace(/\.[^.]+$/, '').replace(/\.+$/, '');
  return `${base || name}.jpg`;
};

// Compress a File for upload. Preserves pixel dimensions, converts PNG to JPEG
// (browser-image-compression white-fills transparency for JPEG output), reduces size via the
// JPEG quality value. Returns the compressed File, or the original if the
// result is not meaningfully smaller. Rejects if the library fails; callers use
// compressOrFallback to turn that into an original-file upload.
export async function compressForUpload(file, opts = {}) {
  const quality = opts.quality ?? DEFAULT_QUALITY;
  const keepOriginalThreshold =
    opts.keepOriginalThreshold ?? KEEP_ORIGINAL_THRESHOLD;

  // Absolute same-origin URL for the browser-image-compression lib
  const libURL = new URL(biclibUrl, document.baseURI).href;

  const blob = await imageCompression(file, {
    alwaysKeepResolution: true, // Preserve the original pixel dimensions; never downscale
    useWebWorker: true, // Do the decode + re-encode in a Web Worker (off the main thread) when the browser supports Worker + OffscreenCanvas
    libURL, // Load the library into the worker from our own origin
    preserveExif: true, //  Keep the image's EXIF metadata (orientation, camera info) in the output instead of stripping it
    fileType: JPEG_MIME_TYPE, // Force the output to JPEG for every input, PNG included
    initialQuality: quality, // The JPEG encoder quality, 0-1, the main size-vs-quality adjustment
  });

  // return the original if it's not meaningfully smaller
  if (blob.size >= file.size * keepOriginalThreshold) {
    return file;
  }

  return new File([blob], toJpgName(file.name), { type: JPEG_MIME_TYPE });
}

// Compress file and return file or returns the original, never rejects.
// Returns { file, compressed, reason, error? }:
//   reason 'compressed'  - a smaller compressed File was produced
//   reason 'not-smaller' - compression ran but did not meaningfully shrink it
//   reason 'error'       - compression threw (error carries the cause)
export async function compressOrFallback(file, opts = {}) {
  try {
    const out = await compressForUpload(file, opts);
    if (out !== file)
      return { file: out, compressed: true, reason: 'compressed' };
    return { file, compressed: false, reason: 'not-smaller' };
  } catch (error) {
    return { file, compressed: false, reason: 'error', error };
  }
}
